import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { FormikValues } from 'formik';

import api from '../../../utils/api';

export type MatricFormatSegmentType = number | string;

export interface MatricFormatSegmentPayload {
  order: number;
  segmentType: MatricFormatSegmentType;
  staticValue?: string;
  padding?: number;
  yearFormat?: string;
}

export interface CreateMatricFormatPayload {
  segments: MatricFormatSegmentPayload[];
}

export interface MatricFormatSegment {
  id: number;
  order: number;
  segmentType: string;
  staticValue: string;
  padding: number;
  yearFormat: string;
}

export interface MatricFormat {
  id: number;
  institutionShortName: string;
  segments: MatricFormatSegment[];
}

export interface MatricFormatMutationResponse {
  statusCode?: number;
  code?: number;
  message: string;
  data?: MatricFormat;
}

export const GET_MATRIC_FORMAT_QUERY_KEY = ['get-matric-format'];

const toMatricFormatPayload = (values: FormikValues | CreateMatricFormatPayload): CreateMatricFormatPayload => ({
  segments: (values?.segments ?? []).map((segment: FormikValues) => ({
    order: Number(segment?.order ?? 0),
    segmentType:
      typeof segment?.segmentType === 'number' ? segment.segmentType : Number(segment?.segmentType ?? 0),
    staticValue: segment?.staticValue,
    padding:
      segment?.padding === null || typeof segment?.padding === 'undefined' || segment?.padding === ''
        ? undefined
        : Number(segment.padding),
    yearFormat: segment?.yearFormat,
  })),
});

export const useCreateMatricFormatMutation = (handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const createMatricFormat = async (payload: CreateMatricFormatPayload): Promise<MatricFormatMutationResponse> => {
    return (await api.post('/MatricFormat/create', payload))?.data;
  };

  const mutation = useMutation({
    mutationKey: ['create-matric-format'],
    mutationFn: createMatricFormat,
  });

  const createMatricFormatHandler = async (values: FormikValues | CreateMatricFormatPayload) => {
    try {
      await mutation.mutateAsync(toMatricFormatPayload(values), {
        onSuccess: async (data) => {
          notification.success({
            message: data?.message || 'Matric format created successfully',
          });

          await queryClient.refetchQueries({
            queryKey: GET_MATRIC_FORMAT_QUERY_KEY,
          });

          handleClose?.();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || error?.response?.data?.title || 'Failed to create matric format',
      });
    }
  };

  return {
    createMatricFormatHandler,
    createMatricFormat: mutation.mutateAsync,
    isCreateLoading: mutation.isPending,
    isLoading: mutation.isPending,
  };
};
